import * as React from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  activeSections,
  isIngestJobFinished,
  isIngestJobNotFoundError,
  sectionCountsFromMembership,
  sectionEntityCounts,
  setSidebarFocus,
  useActiveBranch,
  useAppDispatch,
  useCompareBranchesQuery,
  useGetAuthzMeQuery,
  useGetBranchQuery,
  useGetIngestModificationsQuery,
  useGetIngestQuestionsQuery,
  useGetIngestWriteLogWatermarkQuery,
  useGetMainBranchQuery,
  useGetUnstructuredJobDetailQuery,
  useListIngestSectionsQuery,
  useStopIngestJobMutation,
  useSwitchBranch
} from '@axilon-apps/domains-ontology-ui-state'
import {
  documentUploadCopy,
  documentUploadModeForTarget
} from '../../components/IngestSession/DocumentUploadDialog'
import { useDocumentUpload } from '../../components/IngestSession/DocumentUploadProvider'
import { IngestJobDeleteDialog } from '../../components/IngestSession/IngestJobDeleteDialog'
import { IngestConversationHistory } from '../../components/IngestSession/IngestConversationHistory'
import { type IngestJobState } from '../IngestPage/util/jobStatusMap'
import { CreatedEntities } from './components/CreatedEntities'
import { FileCoverage } from './components/FileCoverage'
import { Scratchpad } from './components/Scratchpad'
import { RunSpine } from './components/RunSpine'
import { LogSection } from './components/LogSection'
import { SectionTabs } from './components/SectionTabs'
import { requestRailExpandOnNextMount } from '../../components/Chat/chat-handoff'
import { newestPendingQuestionId } from '../../components/Chat/sessionTabRules'
import { INGEST_QUESTION_PARAM } from '../../components/IngestSession/ingest-session-route'
import { useChatAppContext } from '../../chat-app-context'
import { useDebugMode } from '../../lib/debug-mode'
import { buildAgentIngestAppContextEntries } from './agent-ingest-app-context'

interface AgentIngestLocationState {
  focusCurrentIngest?: boolean
}

/**
 * Per-job agent activity view for unstructured ingest. Sticky `RunSpine`,
 * two-column body (structure + file coverage), scratchpad, and a collapsed
 * activity log. Driven by durable data polled via
 * `useGetUnstructuredJobDetailQuery` (2 s, stops on terminal). While the
 * ingest branch is live, operator CoT stays on the Current Ingest rail; after
 * the branch is gone, a job-keyed conversation history block appears above
 * the activity log.
 */
const AgentIngestPage = () => {
  const { jobId = '' } = useParams<{ jobId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const activeBranchId = useActiveBranch()
  const { switchBranch } = useSwitchBranch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [jobFinished, setJobFinished] = React.useState(false)
  const [jobDeleted, setJobDeleted] = React.useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [branchSwitchFailed, setBranchSwitchFailed] = React.useState(false)
  const [stopJob, { isLoading: stopping }] = useStopIngestJobMutation()
  const [stopError, setStopError] = React.useState<string | null>(null)
  const canManage = useGetAuthzMeQuery().data?.permissions.includes('ingest/manage') === true
  const debugMode = useDebugMode()
  const { openDocumentUpload, documentTransferOpen } = useDocumentUpload()
  const adoptedJobId = React.useRef<string | null>(null)
  const focusedRailJobId = React.useRef<string | null>(null)
  const focusCurrentIngest = Boolean(
    (location.state as AgentIngestLocationState | null)?.focusCurrentIngest
  )

  const { data: job, error: jobError } = useGetUnstructuredJobDetailQuery(jobId, {
    skip: !jobId,
    refetchOnMountOrArgChange: true
  })

  // Track the job's terminal state directly, including going non-terminal again
  // (e.g. the operator sent a message that re-dispatched a finished run).
  React.useEffect(() => {
    if (job) setJobFinished(isIngestJobFinished(job.state))
  }, [job])
  // A 404 means the job was deleted (here or elsewhere): stop reading its children.
  React.useEffect(() => {
    setJobDeleted(isIngestJobNotFoundError(jobError))
  }, [jobError])

  React.useEffect(() => {
    adoptedJobId.current = null
    focusedRailJobId.current = null
    setBranchSwitchFailed(false)
  }, [jobId])

  // Adopt the job's ingest branch on any visit — sitting on protected main
  // with a parked question is a dead end. One attempt per job so a later
  // branch-picker change is not yanked back.
  React.useEffect(() => {
    if (!job?.branchId || adoptedJobId.current === jobId) return
    if (activeBranchId === job.branchId) {
      adoptedJobId.current = jobId
      setBranchSwitchFailed(false)
      return
    }
    adoptedJobId.current = jobId
    void Promise.resolve(switchBranch(job.branchId)).catch(() => {
      setBranchSwitchFailed(true)
    })
  }, [activeBranchId, job?.branchId, jobId, switchBranch])

  const { data: questions } = useGetIngestQuestionsQuery(jobId, {
    skip: !jobId || jobDeleted,
    refetchOnMountOrArgChange: true
  })

  const {
    data: branch,
    isError: branchUnavailable,
    isFetching: branchFetching
  } = useGetBranchQuery(job?.branchId ?? '', {
    skip: !job?.branchId
  })

  // The section strip: catalog refreshed on job wake-ups; selection rides a
  // `?section=` param so a refresh keeps the tab. An unknown/stale param (the
  // section merged away, a shared link) falls back to "All".
  const { data: sectionRows } = useListIngestSectionsQuery(jobId, {
    skip: !jobId || jobDeleted,
    refetchOnMountOrArgChange: true
  })
  const sections = React.useMemo(() => activeSections(sectionRows), [sectionRows])
  const requestedSection = searchParams.get('section')
  const selectedSection =
    requestedSection && sections.some((s) => s.id === requestedSection) ? requestedSection : null
  const selectSection = (sectionId: string | null): void => {
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        if (sectionId) next.set('section', sectionId)
        else next.delete('section')
        return next
      },
      { replace: true }
    )
  }

  // Per-tab entity counts, derived client-side from the same write log
  // CreatedEntities renders (RTK dedupes the subscription — no extra request).
  // Legacy jobs with no log fall back to the branch compare's stamped rows.
  // Read the cheap watermark; refetch fat modifications only when it moves.
  const { data: writeLogWatermark } = useGetIngestWriteLogWatermarkQuery(jobId, {
    skip: !jobId || jobDeleted || sections.length === 0
  })
  const { data: modifications, refetch: refetchModifications } = useGetIngestModificationsQuery(
    jobId,
    {
      skip: !jobId || sections.length === 0
    }
  )
  const watermarkKey =
    writeLogWatermark === undefined
      ? undefined
      : `${writeLogWatermark.count}:${writeLogWatermark.maxId}`
  const lastWatermarkKeyRef = React.useRef<string | undefined>(undefined)
  React.useEffect(() => {
    lastWatermarkKeyRef.current = undefined
  }, [jobId])
  React.useEffect(() => {
    if (watermarkKey === undefined) return
    const prev = lastWatermarkKeyRef.current
    lastWatermarkKeyRef.current = watermarkKey
    if (prev !== undefined && prev !== watermarkKey) {
      void refetchModifications()
    }
  }, [watermarkKey, refetchModifications])
  const noLog = modifications?.hasLog === false
  const { data: mainBranch } = useGetMainBranchQuery()
  const { data: compare } = useCompareBranchesQuery(
    { targetId: mainBranch?.id ?? '', sourceId: job?.branchId ?? '' },
    { skip: !noLog || !mainBranch?.id || !job?.branchId || sections.length === 0 }
  )
  const entityCounts = React.useMemo(
    () =>
      modifications?.hasLog
        ? sectionCountsFromMembership(modifications.sectionsByNode)
        : sectionEntityCounts(compare?.truncated ? [] : (compare?.nodes?.added ?? [])),
    [modifications, compare]
  )

  useChatAppContext(
    buildAgentIngestAppContextEntries({
      jobId,
      fileName: job?.fileName,
      state: job?.state,
      branchName: branch?.name,
      branchId: job?.branchId,
      selectedSectionId: selectedSection,
      selectedSectionName: sections.find((s) => s.id === selectedSection)?.name
    }),
    ['jobId', 'section']
  )

  const logCount = job?.logCount ?? job?.logs.length ?? 0
  const pending = questions?.pending ?? []
  const answered = questions?.answered ?? []
  const messages = questions?.messages ?? []
  const pendingQuestionCount = pending.length
  const pendingQuestionId = newestPendingQuestionId(pending)
  const searchQuestion = searchParams.get(INGEST_QUESTION_PARAM)
  const shouldFocusRail =
    focusCurrentIngest || Boolean(searchQuestion) || Boolean(pendingQuestionId)
  const latestLog = job?.latestLog ?? job?.logs.at(-1)
  const currentAction = latestLog?.message.split('\n')[0]

  React.useEffect(() => {
    if (
      !shouldFocusRail ||
      focusedRailJobId.current === jobId ||
      job?.id !== jobId ||
      !job.branchId ||
      activeBranchId !== job.branchId
    ) {
      return
    }
    focusedRailJobId.current = jobId
    dispatch(setSidebarFocus({ kind: 'session', id: jobId }))
  }, [activeBranchId, dispatch, job?.branchId, job?.id, jobId, shouldFocusRail])

  const focusPendingQuestion = (): void => {
    requestRailExpandOnNextMount()
    dispatch(setSidebarFocus({ kind: 'session', id: jobId }))
    const questionId = searchQuestion || pendingQuestionId
    if (!questionId) return
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        next.set(INGEST_QUESTION_PARAM, questionId)
        return next
      },
      { replace: true }
    )
  }

  // Reuse the existing branch merge-review flow (the same `/branches/merge`
  // surface `BranchMergeButton` opens): the page opens/previews/merges the
  // request for the job branch — nothing ingest-specific to build here.
  const openMergeReview = (): void => {
    if (!job?.branchId) return
    const params = new URLSearchParams({
      source: job.branchId,
      returnTo: `/ingest/jobs/${jobId}/agent`,
      returnLabel: 'Ingest job'
    })
    navigate(`/branches/merge?${params.toString()}`)
  }

  const documentMode = documentUploadModeForTarget(
    job?.state,
    branch,
    branchUnavailable || Boolean(job && !job.branchId)
  )
  const canReuseJobForDocuments = documentMode === 'add'
  // Current Ingest covers the live branch; once the job's branch is gone (or
  // never existed), surface the persisted transcript here so it stays reviewable.
  const branchGoneForHistory =
    !job?.branchId || branchUnavailable || (branch !== undefined && branch.status !== 'active')
  const showConversationHistory = Boolean(
    jobId && job?.conversationId && branchGoneForHistory && !(job.branchId && branchFetching)
  )
  const jobState = (job?.state ?? 'processing') as IngestJobState
  const historyEndStatus =
    job?.state === 'error' ? 'failed' : job?.state === 'cancelled' ? 'aborted' : 'done'

  const stopIngest = (): void => {
    if (!jobId || stopping) return
    void stopJob(jobId)
      .unwrap()
      .then(() => setStopError(null))
      .catch(() => {
        setStopError('Stop failed — the job may still be running.')
      })
  }

  if (jobDeleted) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-2 py-8">
        <p className="m-0 text-sm text-muted-foreground" role="status">
          This ingest job was deleted.
        </p>
        <Link to="/ingest" className="w-fit text-xs text-st-info hover:underline">
          Back to ingest
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
      {branchSwitchFailed && job?.branchId && activeBranchId !== job.branchId ? (
        <p className="m-0 text-xs text-st-warn" role="alert">
          Couldn&apos;t switch to the ingest branch. Switch from the branch picker to answer.
        </p>
      ) : null}
      {stopError ? (
        <p className="m-0 text-xs text-destructive" role="alert">
          {stopError}
        </p>
      ) : null}
      <RunSpine
        jobId={jobId}
        fileName={job?.fileName}
        branchId={job?.branchId}
        branchName={branch?.name}
        state={jobState}
        submittedAt={job?.submittedAt}
        updatedAt={job?.updatedAt}
        pendingQuestionCount={pendingQuestionCount}
        currentAction={currentAction}
        onNeedsInput={focusPendingQuestion}
        onAddDocuments={() => {
          openDocumentUpload({
            mode: documentMode,
            jobId,
            ...(canReuseJobForDocuments ? { contextJobId: jobId } : {})
          })
        }}
        documentActionLabel={documentUploadCopy(documentMode).title}
        documentTransferOpen={documentTransferOpen}
        onReview={openMergeReview}
        onStop={stopIngest}
        stopping={stopping}
        onDelete={debugMode ? () => setDeleteDialogOpen(true) : undefined}
        canManage={canManage}
      />

      <SectionTabs
        sections={sections}
        selected={selectedSection}
        onSelect={selectSection}
        entityCounts={entityCounts}
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <section className="flex min-w-0 flex-col gap-2.5">
          <h2 className="m-0 px-0.5 text-11 font-medium tracking-[0.08em] text-muted-foreground uppercase">
            Proposed Structure
          </h2>
          <CreatedEntities jobId={jobId} branchId={job?.branchId} sectionId={selectedSection} />
        </section>

        <aside className="flex min-w-0 flex-col gap-2.5 xl:self-start">
          <h2 className="m-0 px-0.5 text-11 font-medium tracking-[0.08em] text-muted-foreground uppercase">
            File Coverage
          </h2>
          <FileCoverage jobId={jobId} active={!jobFinished} />
        </aside>
      </div>

      <section className="flex min-w-0 flex-col gap-2.5">
        <h2 className="m-0 px-0.5 text-11 font-medium tracking-[0.08em] text-muted-foreground uppercase">
          Scratchpad
        </h2>
        <Scratchpad jobId={jobId} sectionId={selectedSection} />
      </section>

      {showConversationHistory ? (
        <IngestConversationHistory
          jobId={jobId}
          conversationId={job?.conversationId ?? ''}
          branchId={job?.branchId}
          answered={answered}
          messages={messages}
          sections={sections}
          fileName={job?.fileName}
          startedAt={job?.submittedAt}
          additionalContext={
            typeof job?.payload?.['additionalContext'] === 'string'
              ? job.payload['additionalContext']
              : undefined
          }
          jobEndStatus={historyEndStatus}
        />
      ) : null}

      <LogSection jobId={jobId} logCount={logCount} />

      <IngestJobDeleteDialog
        jobId={jobId}
        fileName={job?.fileName}
        branchId={job?.branchId}
        jobState={jobState}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onDeleted={() => navigate('/ingest', { replace: true })}
      />
    </div>
  )
}

export default AgentIngestPage
